import { useCallback, useEffect, useRef, useState } from 'react';
import { Keyboard } from 'react-native';
import { useFocusEffect } from 'expo-router';
import { getAuthStore } from '@/lib/api/client';

// Presentation belongs to the initiating screen, not to a future chat ID.
// Leaving the screen invalidates callbacks; the transport retains its durable
// request for recovery if a launch reply is lost after submission.
export default function useRemoteLaunchFeedback() {
  const [launch, setLaunch] = useState(null);
  const generation = useRef(0);
  const active = useRef(false);
  const reset = useCallback(() => { generation.current++; active.current = false; setLaunch(null); }, []);
  useFocusEffect(useCallback(() => {
    reset();
    return () => { generation.current++; };
  }, [reset]));
  useEffect(() => getAuthStore().subscribe((next, previous) => {
    if (!next.token || next._isSigningOut || next.user?.id !== previous.user?.id ||
        next._sessionGeneration !== previous._sessionGeneration) reset();
  }), [reset]);
  const begin = useCallback(({ prompt, fileIds }) => {
    if (active.current) return null;
    active.current = true;
    const request = ++generation.current;
    const auth = getAuthStore().getState();
    const userId = auth.user?.id, session = auth._sessionGeneration;
    const isRequestCurrent = () => {
      const current = getAuthStore().getState();
      return request === generation.current && !!current.token && !current._isSigningOut &&
        current.user?.id === userId && current._sessionGeneration === session;
    };
    return { isRequestCurrent, onProgress: progress => {
      if (!isRequestCurrent()) return;
      Keyboard.dismiss();
      setLaunch({ ...progress, prompt, fileCount: fileIds?.length || 0 });
    } };
  }, []);
  return { launch, begin, reset };
}
